import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * "ClickUp-style" sticky horizontal scrollbar — pinned to the bottom
 * of the viewport, aligned with a target scroll container.
 *
 * Originally inlined inside `pages/employee/SpacePage.jsx`'s Gantt
 * for the timeline. Lifted into a shared component so the same
 * scrollbar behaviour ships on every Gantt-style page (Team Tasks,
 * My Tasks, Task Monitor Gantt) without three copies of the same
 * scroll-sync + ResizeObserver dance.
 *
 * How it works:
 *
 *   1. The page renders its own scrollable timeline container with
 *      its native scrollbar HIDDEN (Tailwind:
 *      `[scrollbar-width:none] [&::-webkit-scrollbar]:hidden`).
 *   2. The page passes the container's ref + the total inner content
 *      width into this component.
 *   3. This component portals a `position: fixed` scrollbar to
 *      `document.body`, anchored to the viewport bottom, sized + left-
 *      positioned to perfectly overlay the timeline column.
 *   4. ResizeObserver re-measures whenever the page reflows (sidebar
 *      collapses, window resizes, etc.).
 *   5. Scroll events on either side are mirrored to the other, guarded
 *      by `syncingScrollRef` to prevent a ping-pong loop.
 *   6. An optional `onScroll` callback lets the consumer plug in their
 *      own logic (e.g. infinite-scroll edge detection on the Spaces
 *      Gantt).
 *
 * Props:
 *
 *   timelineRef    — React ref pointing at the timeline's scrollable
 *                    `<div>` element. The caller controls its width
 *                    + overflow style; this component only reads
 *                    `scrollLeft`, `scrollWidth`, etc.
 *   timelineWidth  — numeric pixel width of the timeline's inner
 *                    content. Drives the fake-scrollbar inner spacer
 *                    so the bar's thumb size matches the timeline.
 *   onScroll       — optional callback fired when the scrollbar (or
 *                    the timeline, mirrored through the scrollbar)
 *                    scrolls. Receives the synthetic event.
 *
 * Renders nothing visible until the page has measured the timeline's
 * bounds at least once. After that, returns a portal containing the
 * fixed scrollbar.
 */
export function StickyTimelineScrollbar({
  timelineRef,
  timelineWidth,
  onScroll,
}) {
  const scrollbarRef = useRef(null)
  const syncingScrollRef = useRef(false)
  const [bounds, setBounds] = useState(null)

  // Measure the timeline's viewport position whenever the layout
  // changes. ResizeObserver catches the common cases (sidebar
  // collapsing, sub-panel resizing); window resize catches the
  // edge cases where the pane's `left` shifts without its own size
  // changing.
  useEffect(() => {
    const el = timelineRef.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      setBounds({ left: rect.left, width: rect.width })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [timelineRef])

  // Mirror timeline → scrollbar. We listen on the timeline element
  // directly because the consumer may not pass us an onScroll handler.
  useEffect(() => {
    const el = timelineRef.current
    if (!el) return
    function handleScroll() {
      if (syncingScrollRef.current) return
      syncingScrollRef.current = true
      if (scrollbarRef.current) {
        scrollbarRef.current.scrollLeft = el.scrollLeft
      }
      requestAnimationFrame(() => {
        syncingScrollRef.current = false
      })
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [timelineRef])

  if (!bounds) return null

  return createPortal(
    <div
      ref={scrollbarRef}
      onScroll={(e) => {
        // Mirror scrollbar → timeline.
        if (!syncingScrollRef.current) {
          syncingScrollRef.current = true
          const t = timelineRef.current
          if (t) t.scrollLeft = e.currentTarget.scrollLeft
          requestAnimationFrame(() => {
            syncingScrollRef.current = false
          })
        }
        onScroll?.(e)
      }}
      className="fixed z-30 overflow-x-auto"
      style={{
        bottom: 0,
        left: bounds.left,
        width: bounds.width,
      }}
      aria-label="Timeline horizontal scrollbar"
    >
      {/* Inner spacer — its width tells the browser how wide the
       *  thumb should be relative to the viewport. Height drives
       *  the scrollbar track's vertical footprint. */}
      <div
        style={{ width: timelineWidth, height: 12 }}
        aria-hidden="true"
      />
    </div>,
    document.body,
  )
}
